<script lang="ts" setup>
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import type { VForm } from "vuetify/components";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import TimezoneSelect from "@/components/selects/TimezoneSelect.vue";
import FileUploadingGrid from "@/views/trips/components/FileUploadingGrid.vue";
import PodDetails from "@/views/trips/components/PodDetails.vue";
import { API } from "@/api/api";
import { useBaseView, useDateTime } from "@/composables";
import { useError } from "@/composables/useError";
import { useValidation } from "@/composables/useValidation";
import type { RouteStopDto, UnloadRouteStopBol } from "@/api/trips/dto/trip.dto";
import type { StopInfoFormProps } from "@/types/props";
import type { DeliveryInfoFormData, StopInfoFile, TripRouteStop } from "@/types/trip";

const { t } = useI18n();

const loading = defineModel<boolean>("loading", { default: false });

const props = withDefaults(defineProps<StopInfoFormProps>(), {
  preload: false,
  readonly: false,
});

const emits = defineEmits<{
  (e: "save"): void;
}>();

const api = new API();

const { dateToUTC, timezonesShortList } = useDateTime();
const { getPreviewPath } = useBaseView();
const { requiredValidator } = useValidation();
const { showAPIError, scrollToError } = useError();

const formRef = useTemplateRef<VForm | null>("form");
const unloadData = ref<DeliveryInfoFormData>({
  date: "",
  time: "",
  timezone: "",
  bols: [],
  podFiles: [],
  files: [],
});

const routeStop = ref<TripRouteStop | RouteStopDto>(props.stop);

const podFilesRequired = computed<boolean>(() => Boolean(unloadData.value.bols.length));

const isReload = computed(() => {
  return props.stop.isReload;
});

async function loadRouteStop(): Promise<void> {
  if (!props.stop.id) {
    return;
  }

  try {
    const response = await api.trips.getRouteStop(props.tripId, String(props.stop.id));

    if (response.success && response.data) {
      routeStop.value = response.data;
    } else {
      showAPIError(response, t("trip.route-stop-loading-failed"));
    }
  } catch (error) {
    console.error(error);
  }
}

async function init(): Promise<void> {
  if (!props.preload) {
    unloadData.value.timezone = routeStop.value.facility?.timezone ?? "";
    const date = dayjs().tz(unloadData.value.timezone);
    unloadData.value.date = date.format("YYYY-MM-DD");
    unloadData.value.time = date.format("HH:mm");

    return;
  }

  unloadData.value.timezone = routeStop.value.timezone ?? "";
  const date = dayjs(routeStop.value.loadUnloadTime).tz(unloadData.value.timezone);
  unloadData.value.date = date.format("YYYY-MM-DD");
  unloadData.value.time = date.format("HH:mm");

  try {
    const response = await api.trips.getTripFiles(props.tripId);

    if (response.success && response.data) {
      const stopFiles = response.data
        .filter((f) => f.tags?.includes("route_stop:" + routeStop.value.id))
        .map((f) => ({
          ...f,
          previewPath: getPreviewPath(f),
        }));

      unloadData.value.podFiles = stopFiles.filter((f) => f.tags?.includes("pod"));
      unloadData.value.files = stopFiles.filter((f) => f.tags?.includes("other"));
    }
  } catch (error) {
    console.error(error);
  }
}

async function uploadFiles(files: StopInfoFile[], fileType: string, withPreviewPath: boolean): Promise<void> {
  const uploadPromises = files.map(async (uploadingFile) => {
    uploadingFile.loading = true;

    try {
      const response = await api.trips.uploadRouteStopFile(
        props.tripId,
        String(routeStop.value.id),
        fileType,
        uploadingFile.file!,
        "dummy",
      );

      if (response.success && response.data) {
        Object.assign(
          uploadingFile,
          withPreviewPath ? { ...response.data, previewPath: getPreviewPath(response.data) } : response.data,
        );
      } else {
        uploadingFile.error = true;
      }
    } catch (error) {
      console.error(error);
      uploadingFile.error = true;
    } finally {
      uploadingFile.loading = false;
    }
  });

  await Promise.all(uploadPromises);
}

async function save(): Promise<void> {
  const result = await formRef.value?.validate();

  if (!result?.valid) {
    scrollToError(formRef.value?.$el);

    return;
  }

  loading.value = true;

  await Promise.all([
    uploadFiles(unloadData.value.podFiles, "pod", true),
    uploadFiles(unloadData.value.files, "other", false),
  ]);

  const bolsPayload: UnloadRouteStopBol[] = unloadData.value.bols.map((bol) => ({
    id: bol.id,
    signedBy: bol.signedBy,
  }));

  try {
    const response = await api.trips.unloadRouteStop(props.tripId, String(routeStop.value.id), {
      unloadTime: dateToUTC(unloadData.value.date + " " + unloadData.value.time, unloadData.value.timezone),
      podFiles: unloadData.value.podFiles,
      otherFiles: unloadData.value.files,
      bols: bolsPayload,
    });

    if (response.success) {
      emits("save");
    } else {
      showAPIError(response, "Error unload verification at stop");
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  loading.value = true;

  await loadRouteStop();
  await init();

  loading.value = false;
});

defineExpose({ save });
</script>

<template>
  <v-form ref="form">
    <div class="mb-6">
      <pod-details v-model="unloadData.bols" :readonly="readonly" :route="route" :stop="routeStop" />
    </div>
    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Attached files</div>

      <template v-if="!isReload">
        <div class="text-grey-darken-1 font-weight-500 mb-4">
          POD files<span v-if="podFilesRequired" class="text-error">*</span>
        </div>
        <file-uploading-grid
          v-model="unloadData.podFiles"
          :columns="3"
          :disabled="readonly"
          :required="podFilesRequired"
        ></file-uploading-grid>
      </template>

      <div class="text-grey-darken-1 font-weight-500 mb-4 mt-6">Other files</div>
      <file-uploading-grid v-model="unloadData.files" :columns="3" :disabled="readonly"></file-uploading-grid>
    </div>
    <div>
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Time</div>
      <v-row>
        <v-col cols="3">
          <timezone-select
            v-model="unloadData.timezone"
            :disabled="readonly"
            :items="timezonesShortList"
            :rules="[requiredValidator]"
            required
          />
        </v-col>
        <v-col cols="6">
          <date-picker v-model="unloadData.date" :disabled="readonly" label="Date" required></date-picker>
        </v-col>
        <v-col cols="3">
          <time-picker v-model="unloadData.time" :disabled="readonly" label="Time" required></time-picker>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<style lang="scss" scoped></style>
